import { useInfiniteQuery, useQuery, type QueryClient } from "@tanstack/react-query";
import { isApiError } from "./client";
import { invalidateCampaigns } from "./rewardsQueries";
import { fetchAdvertiserStats, fetchBillboardAvailability, fetchBillboards, fetchMyAdvertisers, fetchMyCreatives, fetchMyStores, fetchReviewQueue, type ReviewQueueState } from "./advertising";

/** Query keys and hooks for the advertiser area (ADR-0009). */
export const adKeys = {
  myAdvertisers: ["advertising", "my-advertisers"] as const,
  myCreatives: ["advertising", "my-creatives"] as const,
  billboards: ["billboards"] as const,
  availabilityAll: ["billboard-availability"] as const,
  availability: (billboardId: string, from: string, to: string) => ["billboard-availability", billboardId, from, to] as const,
  stats: ["advertising", "stats"] as const,
  reviewQueue: ["review", "creatives"] as const,
  myStores: ["advertising", "my-stores"] as const,
};

const noAuthRetry = (count: number, err: unknown) => !(isApiError(err) && [401, 403, 404].includes(err.status)) && count < 2;

export function useMyAdvertisersQuery(signedIn: boolean) {
  return useQuery({ queryKey: adKeys.myAdvertisers, queryFn: fetchMyAdvertisers, enabled: signedIn, staleTime: 30_000, retry: noAuthRetry });
}

/** Library: pending creatives may be approved by staff at any time, so it is re-read every minute while shown. */
export function useMyCreativesQuery(signedIn: boolean) {
  return useQuery({ queryKey: adKeys.myCreatives, queryFn: fetchMyCreatives, enabled: signedIn, staleTime: 15_000, refetchInterval: signedIn ? 60_000 : false, retry: noAuthRetry });
}

/** Safety net between UTC midnights and notifications (a staff revoke swaps a board immediately on the server). */
export const BILLBOARDS_REFRESH_MS = 5 * 60_000;

export function useBillboardsQuery(enabled = true) {
  return useQuery({ queryKey: adKeys.billboards, queryFn: fetchBillboards, enabled, staleTime: 30_000, refetchInterval: enabled ? BILLBOARDS_REFRESH_MS : false, retry: noAuthRetry });
}

export function useBillboardAvailabilityQuery(billboardId: string | null, range: { from: string; to: string } | null) {
  return useQuery({
    queryKey: adKeys.availability(billboardId ?? "none", range?.from ?? "", range?.to ?? ""),
    queryFn: () => fetchBillboardAvailability(billboardId as string, range!.from, range!.to),
    enabled: Boolean(billboardId && range),
    staleTime: 10_000,
    // Other buyers' holds come and go: keep the calendar honest while it is open.
    refetchInterval: billboardId && range ? 30_000 : false,
    retry: noAuthRetry,
  });
}

/** Stores this account owns right now (re-read when a picker opens: a takeover can happen any time). */
export function useMyStoresQuery(signedIn: boolean) {
  return useQuery({ queryKey: adKeys.myStores, queryFn: fetchMyStores, enabled: signedIn, staleTime: 0, refetchOnMount: "always", retry: noAuthRetry });
}

export function useAdvertiserStatsQuery(signedIn: boolean) {
  return useQuery({ queryKey: adKeys.stats, queryFn: fetchAdvertiserStats, enabled: signedIn, staleTime: 30_000, retry: noAuthRetry });
}

export function useReviewQueueQuery(enabled: boolean, state: ReviewQueueState = "pending") {
  return useInfiniteQuery({
    // Prefix adKeys.reviewQueue so invalidating it refreshes both lists.
    queryKey: [...adKeys.reviewQueue, state],
    queryFn: ({ pageParam }) => fetchReviewQueue(pageParam, state),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    retry: noAuthRetry,
  });
}

/** After a creative, placement or billboard change: re-read everything that shows it. */
export function invalidateAdvertising(qc: QueryClient, what: { creatives?: boolean; billboards?: boolean; stats?: boolean; properties?: boolean; campaigns?: boolean } = {}): void {
  const all = Object.keys(what).length === 0;
  if (all || what.creatives) void qc.invalidateQueries({ queryKey: adKeys.myCreatives });
  if (all || what.billboards) {
    void qc.invalidateQueries({ queryKey: adKeys.billboards });
    void qc.invalidateQueries({ queryKey: adKeys.availabilityAll });
  }
  if (all || what.stats) void qc.invalidateQueries({ queryKey: adKeys.stats });
  if (all || what.properties) {
    void qc.invalidateQueries({ queryKey: ["properties"] });
    void qc.invalidateQueries({ queryKey: ["property"] });
    void qc.invalidateQueries({ queryKey: adKeys.myStores });
  }
  if (all || what.campaigns) invalidateCampaigns(qc);
}
